import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../../network/index";

import Button from "@mui/material/Button";
import { CircularProgress } from "@mui/material";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import OrderDataTable from "./OrderDataTable";
import OrderDetailInfo from "./OrderDetailInfo";
import ReturnDetailsCard from "./ReturnDetailsCard";

import {
  ORDER_STATE_PLACED,
  ORDER_STATE_ACCEPTED,
  ORDER_STATE_FULFILLED,
  ORDER_STATE_CANCELLED,
  ORDER_STATE_AGENT_ASSIGNED,
  ORDER_STATE_OUT_FOR_DELIVERY,
  ORDER_STATE_PICKED_UP,
  ORDER_STATE_DELIVERED,
  MARK_AS_PACKED,
  ACCEPT,
  REJECT,
  ORDER_DETAILS,
  DELIVERED,
  OUT_FOR_DELIVERY,
  AGENT_ASSIGNED,
  PICKED_UP,
} from "../../../constants/string";

import { Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LottieError from "../../../components/LottieError";
import SnackbarMessage from "../../../components/SnackbarMessage";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useWarehouse } from "../../../contexts/WarehouseContext";
import CommentBox from "./CommentBox";

const OrderDetails = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState({ orderData: {}, ondcOrderResponse: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [partialCancel, setPartialCancel] = useState({});
  const theme = useTheme();
  const [status, setStatus] = useState("");
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [changeStateButtonLoading, setChangeStateButtonLoading] =
    useState(false);
  const [rejectButtonLoading, setRejectButtonLoading] = useState(false);
  const navigate = useNavigate();
  const { warehouseId } = useWarehouse();
  const prevWarehouseIdRef = useRef(warehouseId);
  const [isCommentBoxVisible, setCommentBoxVisible] = useState(false);

  useEffect(() => {
    axios
      .post("/orders/view/" + orderId + "/1")
      .then((data) => {
        if (data.data?.orderData) {
          setOrder(data.data);
        } else {
          setIsError(true);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        setIsError(true);
      });
  }, [status]);

  useEffect(() => {
    if (order && order.orderData.Orderstate) {
      setStatus(order.orderData.Orderstate.namealias);
    }
  }, [order]);

  useEffect(() => {
    if(warehouseId !== prevWarehouseIdRef.current) {
      navigate('/');
    }
  }, [warehouseId, navigate]);

  const changeOrderState = (order, nextStateNumber, updateStateTo) => {
    setChangeStateButtonLoading(true);
    let formData = new FormData();
    formData.append("data[fromoutlet_id]", order.orderData.fromOutlet.id);
    formData.append("data[neworderstate_id]", parseInt(nextStateNumber));
    formData.append("data[oldorderstate_id]", order.orderData.Orderstate.id);
    formData.append("data[order_id]", order.orderData.Order.id);
    if (Object.keys(partialCancel).length !== 0 && status === ORDER_STATE_PLACED) {
      formData.append("data[partialCancel]", JSON.stringify(partialCancel));
    }

    axios
      .post("/orders/changeOrderState/", formData)
      .then((data) => {
        setStatus(updateStateTo);
        setChangeStateButtonLoading(false);
      })
      .catch((error) => {
        handleOpenSnackBar();
        setChangeStateButtonLoading(false);
        console.log(error);
      });
  };

  const rejectOrder = (order, nextStateNumber, updateStateTo, rejectReason) => {
    setRejectButtonLoading(true);
    let formData = new FormData();
    formData.append("data[neworderstate_id]", parseInt(nextStateNumber));
    formData.append("data[oldorderstate_id]", order.orderData.Orderstate.id);
    formData.append("data[order_id]", order.orderData.Order.id);
    formData.append("data[uiRequest]", true);
    formData.append("data[Rejectedorder][reason]", rejectReason.name);
    formData.append("data[Rejectedorder][reason_id]", rejectReason.id);
    axios
      .post("/orders/reject/" + orderId + "/1", formData)
      .then((data) => {
        setStatus(updateStateTo);
        setRejectButtonLoading(false);
      })
      .catch((error) => {
        handleOpenSnackBar();
        setRejectButtonLoading(false);
        console.log(error);
      });
  };

  const fulfillOrder = (order, nextStateNumber, updateStateTo) => {
    setChangeStateButtonLoading(true);
    let formData = new FormData();
    formData.append("data[outlet_id]", order.orderData.fromOutlet.id);
    formData.append("data[neworderstate_id]", parseInt(nextStateNumber));
    formData.append("data[oldorderstate_id]", order.orderData.Orderstate.id);
    formData.append("data[order_id]", order.orderData.Order.id);
    formData.append("data[isOndcFulfillment]", 1);
    formData.append("[data[isJson]", true);

    axios
      .post("/orders/fulfil/" + orderId, formData)
      .then((data) => {
        setStatus(updateStateTo);
        setChangeStateButtonLoading(false);
      })
      .catch((error) => {
        handleOpenSnackBar();
        setChangeStateButtonLoading(false);
        console.log(error);
      });
  };

  const handleAccept = (order) => {
    changeOrderState(order, 2, ORDER_STATE_ACCEPTED);
  };

  const handleReject = () => {
    setCommentBoxVisible(true);
  };
  
  const handleRejectOnSubmit = (rejectReason) => {
    rejectOrder(order, 4, ORDER_STATE_CANCELLED,rejectReason);
  }
  const handleClose = () => {
    setCommentBoxVisible(false);
  };
  const handlePacked = () => {
    fulfillOrder(order, 3, ORDER_STATE_FULFILLED);
  };

  const handleOpenSnackBar = () => {
    setShowSnackBar(true);
  };

  const handleCloseSnackBar = () => {
    setShowSnackBar(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderButtons = () => {
    const { selfDelivery, markDeliverInstantly } =
      order.ondcOrderResponse[0] || {};

    if (status === ORDER_STATE_PLACED) {
      return (
        <Stack
          spacing={2}
          direction="row"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: 2,
          }}
        >
          <Button
            variant="outlined"
            color="error"
            onClick={handleReject}
            disabled={rejectButtonLoading}
          >
            {REJECT}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={changeStateButtonLoading}
            onClick={() => handleAccept(order)}
          >
            {ACCEPT}
          </Button>
        </Stack>
      );
    }

    if (status === ORDER_STATE_ACCEPTED) {
      return (
        <Stack
              spacing={2}
              direction="row"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: 2,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleReject}
                disabled={rejectButtonLoading}
              >
                {REJECT}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={changeStateButtonLoading}
                onClick={() => fulfillOrder(order, 3, ORDER_STATE_FULFILLED)}
              >
                {MARK_AS_PACKED}
              </Button>
            </Stack>
      );
    }

    if (selfDelivery) {
      switch (status) {
        case ORDER_STATE_FULFILLED:
          return (
            <Stack
              spacing={2}
              direction="row"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                disabled={changeStateButtonLoading}
                onClick={() =>
                  changeOrderState(order, 9, ORDER_STATE_AGENT_ASSIGNED)
                }
              >
                {AGENT_ASSIGNED}
              </Button>
            </Stack>
          );
        case ORDER_STATE_AGENT_ASSIGNED:
          return (
            <Stack
              spacing={2}
              direction="row"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: 2,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleReject}
                disabled={rejectButtonLoading}
              >
                {REJECT}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={changeStateButtonLoading}
                onClick={() =>
                  changeOrderState(order, 10, ORDER_STATE_PICKED_UP)
                }
              >
                {PICKED_UP}
              </Button>
            </Stack>
          );
        case ORDER_STATE_PICKED_UP:
          return (
            <Stack
              spacing={2}
              direction="row"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: 2,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleReject}
                disabled={rejectButtonLoading}
              >
                {REJECT}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={changeStateButtonLoading}
                onClick={() =>
                  changeOrderState(order, 11, ORDER_STATE_OUT_FOR_DELIVERY)
                }
              >
                {OUT_FOR_DELIVERY}
              </Button>
            </Stack>
          );
        case ORDER_STATE_OUT_FOR_DELIVERY:
          return (
            <Stack
              spacing={2}
              direction="row"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: 2,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleReject}
                disabled={rejectButtonLoading}
              >
                {REJECT}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={changeStateButtonLoading}
                onClick={() =>
                  changeOrderState(order, 12, ORDER_STATE_DELIVERED)
                }
              >
                {DELIVERED}
              </Button>
            </Stack>
          );
        default:
          return null;
      }
    }

    return null;
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "80% ",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <LottieError />;
  }

  return (
    <React.Fragment sx={{ background: theme.palette.background.main }}>
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative",
        }}
      >
        <ArrowBackIosIcon
           style={{ 
            position: 'absolute', 
            marginLeft: 8
          }} 
          onClick={handleBack}
        />
        <Typography
          variant="h2"
          component="div"
          sx={{
            fontWeight: "bold",
            textAlign: "left",
            padding: "2vh",
            marginLeft: 2
          }}
        >
          {ORDER_DETAILS} {""}
          <Typography variant="body1" color="text.secondary" component="span">
            (ID: {order.orderData.Order.id})
          </Typography>
        </Typography>
      </Box>
      <OrderDetailInfo
        ondcOrder={order.ondcOrderResponse[0]}
        orderState={order.orderData.Orderstate.namealias}
      />

      <OrderDataTable
        order={order}
        setPartialCancel={setPartialCancel}
        partialCancel={partialCancel}
      />

      {order.ondcOrderResponse[0]?.saleReturnDetails?.length > 0 && (
        <ReturnDetailsCard
          returnDetails={order.ondcOrderResponse[0].saleReturnDetails}
          orderId={orderId}
          outletId={order.orderData.Outlet.id}
          orderData={order.orderData.orderdetail}
          refreshOrder={() => {
            // Logic to refresh the order data
            setStatus((prevStatus) => prevStatus + 1);
          }}
        />
      )}

      {renderButtons()}

      {isCommentBoxVisible && (
        <>
          <Box style={styles.overlay}/>
          <Box style={styles.commentBoxContainer}>
            <CommentBox 
              handleRejectOnSubmit = { handleRejectOnSubmit } 
              handleClose = { handleClose } 
            />
          </Box>
        </>
      )}
      <SnackbarMessage
        open={showSnackBar}
        message="Order state could not change. Please try again!"
        severity="error"
        onClose={handleCloseSnackBar}
      />
    </React.Fragment>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 999,
    pointerEvents: "auto",
  },
  commentBoxContainer: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1000,
    width: "80%", 
    maxWidth: "600px", 
    height: "300px",
  },
};

export default OrderDetails;
